// react
import { FormEvent, SyntheticEvent, useContext, useEffect, useState } from 'react';

// router
import { Link as RouterLink, Outlet, useSearchParams, useLocation } from 'react-router-dom';

// types
import {
	Account,
	Category,
	Component,
	ExpenseType,
	LoadingStatus,
	Loan,
	Option,
	Pagination,
	TransactionGroup,
	TransactionWithDaEquivalent,
	WithdrawalApplication,
} from '../../../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// hooks
import { useFetch } from '../../../../../hooks/useFetch';

// pages
import { SoeFilters } from './Filters/Filters';

// mui
import {
	Button,
	Container,
	Grid,
	SelectChangeEvent,
	ToggleButton,
	ToggleButtonGroup,
	Typography,
} from '@mui/material';
import { theme } from '../../../../../theme';

// forms
import { FormSearch } from '../../../../forms/FormSearch/FormSearch';

// dialogs
import { DialogExportSoeReport } from '../../../../dialogs/DialogExportSoeReport/DialogExportSoeReport';
import { DialogExportSoeReportSummary } from '../../../../dialogs/DialogExportSoeReportSummary/DialogExportSoeReportSummary';

// ui
import { FilterProps } from '../../../../ui/Filters/Filters';
import { ListItemTextAccount } from '../../../../ui/ListItemTextAccount/ListItemTextAccount';

// context
import { ContextProject } from '../../Project.context';
import { ContextUser, ContextUserEntity } from '../../../../../App.context';
import { ContextSoeAccounts, ContextSoeFilters, ContextSoeTransactions } from './Soe.context';

// utils
import { defaultPagination } from '../../../../../utils/defaults';
import { formatDate } from '../../../../../utils/dates';
import { getHeaderSelections } from '../../../../tables/basic/TableBasicSoe/TableBasicSoe.headers';

// icons
import {
	SummarizeOutlined as IconSummarize,
	TableChartOutlined as IconTableChart,
} from '@mui/icons-material';

// ui
import { BtnCheckboxList, CheckboxListItem } from '../../../../ui/BtnCheckboxList/BtnCheckboxList';
import { BtnSortableList } from '../../../../ui/BtnSortableList/BtnSortableList';

// utils
import { translateTransactionType } from '../../../../../utils/translations';
import { addDaEquivalentPropsToTransactions } from '../../../../../utils/transactions';

// props
export type ChangeFilterEvent = SyntheticEvent<Element, Event> | SelectChangeEvent<unknown>;

export interface GetTransactionsProps {
	pagination?: Pagination;
	tab?: string;
}

export type FiltersValues = Record<string, string | string[]>;

export const Soe = () => {
	// hooks
	const location = useLocation();
	const [searchParams, setSearchParams] = useSearchParams();
	const { t } = useTranslation();

	// params
	const paramFromAccount = searchParams.get('fromAccount');
	const paramAmountMax = searchParams.get('amountMax');
	const paramAmountMin = searchParams.get('amountMin');
	const paramCategory = searchParams.get('category')?.split(',');
	const paramComponent = searchParams.get('component')?.split(',');
	const paramCurrency = searchParams.get('currency');
	const paramDialog = searchParams.get('dialog');
	const paramReadOnlyFilters = searchParams.get('readOnlyFilters')?.split(',');
	const paramEndDate = searchParams.get('dateEnd');
	const paramExpenseType = searchParams.get('expenseType')?.split(',');
	const paramGroupColumns = searchParams.get('groupColumns')?.split(',');
	const paramHiddenFilters = searchParams.get('hiddenFilters')?.split(',');
	const paramId = searchParams.get('id');
	const paramLimit = Number(searchParams.get('limit') || 25);
	const paramLoan = searchParams.get('loan');
	const paramLoanCode = searchParams.get('loanCode');
	const paramOrder = searchParams.get('order');
	const paramOrderBy = searchParams.get('orderBy');
	const paramPage = Number(searchParams.get('page') || 1);
	const paramStartDate = searchParams.get('dateStart');
	const paramTags = searchParams.get('tags')?.split(',');
	const paramTxTypes = searchParams.get('type');
	const paramWithdrawalApplication = searchParams.get('withdrawalApplication');
	const paramWithdrawalApplicationNew = searchParams.get('withdrawalApplicationNew');

	const filtersValuesDefaults: FiltersValues = {
		amountMin: paramAmountMin || '',
		amountMax: paramAmountMax || '',
		category: paramCategory || [],
		component: paramComponent || [],
		expenseType: paramExpenseType || [],
		fromAccount: paramFromAccount || '',
		dateEnd: paramEndDate || '',
		dateStart: paramStartDate || '',
		order: paramOrder || '',
		orderBy: paramOrderBy || '',
		loan: paramLoan || '',
		tags: paramTags || [],
		type: paramTxTypes ? paramTxTypes.split(',') : [],
		withdrawalApplication: paramWithdrawalApplication || '',
	};

	// context
	const { project } = useContext(ContextProject);
	const { user } = useContext(ContextUser);
	const { userEntity } = useContext(ContextUserEntity);

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [accounts, setAccounts] = useState<Account[]>([]);
	const [categories, setCategories] = useState<Category[]>([]);
	const [components, setComponents] = useState<Component[]>([]);
	const [columns, setColumns] = useState<CheckboxListItem[]>([
		{
			key: 'component',
			label: t('component'),
			checked: paramGroupColumns ? paramGroupColumns?.includes('component') : true,
		},
		{
			key: 'category',
			label: t('category'),
			checked: paramGroupColumns ? paramGroupColumns?.includes('category') : true,
		},
		{
			key: 'expenseType',
			label: t('expenseType'),
			checked: paramGroupColumns ? paramGroupColumns?.includes('expenseType') : true,
		},
	]);

	const [expenseTypes, setExpenseTypes] = useState<ExpenseType[]>([]);
	const [filtersValues, setFiltersValues] = useState<FiltersValues>(filtersValuesDefaults);
	const [headerSelections, setHeaderSelections] = useState<CheckboxListItem[]>(
		getHeaderSelections({ t })
	);
	const [loans, setLoans] = useState<Loan[]>([]);
	const [paginationTransactions, setPaginationTransactions] = useState<Pagination>({
		...defaultPagination,
		perPage: paramLimit ? Number(paramLimit) : defaultPagination.perPage,
		page: 1,
	});
	const [status, setStatus] = useState<LoadingStatus>('DEFAULT');
	const [transactions, setTransactions] = useState<TransactionWithDaEquivalent[] | []>([]);
	const [transactionsGrouping, setTransactionsGrouping] = useState<TransactionGroup[] | []>([]);
	const [transactionsUnchecked, setTransactionsUnchecked] = useState<number[] | []>([]);
	const [withdrawalApplications, setWithdrawalApplications] = useState<WithdrawalApplication[]>([]);

	// vars
	let activeTab = 'details';

	if (location.pathname.includes('summary')) {
		activeTab = 'summary';
	}

	// fetch accounts
	useFetch({
		isEnabled: true,
		url: `${process.env.REACT_APP_API_URL}/accounts?project=${project?.id}&or=type:=:DESIGNATED,isReimbursement:=:true,&fields=currency,id,institution,name&status=ACTIVE&orderBy=name&order=DESC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setAccounts(res.data);
			}
		},
	});

	// fetch loans
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/loans?project=${project?.id}&fields=code,financier,id,status`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setLoans(res.data);
			}
		},
	});

	// fetch components
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/components?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setComponents(res.data);
			}
		},
	});

	// fetch categories
	useFetch({
		isEnabled: Boolean(project?.id),
		url: `${process.env.REACT_APP_API_URL}/categories?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setCategories(res.data);
			}
		},
	});

	// fetch expense types
	let urlFetchExpenseTypes = `${process.env.REACT_APP_API_URL}/expense-types?project=${project?.id}&fields=id,name&status=ACTIVE&orderBy=name&order=ASC`;

	if (userEntity) {
		urlFetchExpenseTypes += `&entity=null,${userEntity.id}`;
	}

	useFetch({
		isEnabled: Boolean(project?.id && user),
		url: urlFetchExpenseTypes,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setExpenseTypes(res.data);
			}
		},
	});

	// fetch withdrawal applications
	useFetch({
		isEnabled: Boolean(project),
		url: `${process.env.REACT_APP_API_URL}/withdrawal-applications?project=${project?.id}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setWithdrawalApplications(res.data);
			}
		},
	});

	const groupBy = columns
		.filter((c) => c.checked)
		.map((c) => c.key)
		.join(',');

	const getTransactions = async ({ pagination, tab = 'details' }: GetTransactionsProps) => {
		// fetch transactions
		const page = pagination?.page || paramPage || 1;
		const limit = pagination?.perPage || paramLimit || 25;

		let urlFetchTransactions = `${process.env.REACT_APP_API_URL}/transactions?project=${project?.id}&status=!REVERTED`;

		const populateProps = [
			'category',
			'component',
			'designatedAccount',
			'expenseType',
			'payments',
			'payments.conversions',
		];

		if (tab === 'details') {
			populateProps.push(
				'contract',
				'fromAccount',
				'fromEntity',
				'payments.loan',
				'refunds',
				'refunds.payments',
				'refunds.payments.conversions',
				'refunds.payments.loan',
				'toAccount',
				'toEntity',
				'withdrawalApplication'
			);
			urlFetchTransactions += `&page=${page}&limit=${limit}`;
		} else if (tab === 'summary') {
			urlFetchTransactions += `&groupBy=${groupBy}`;
		}

		urlFetchTransactions += `&populate=${populateProps.join(',')}`;

		// define filters keys
		const filtersKeys = Object.keys(filtersValues);

		// add each filter value to url
		for (let i = 0; i < filtersKeys.length; i++) {
			const key = filtersKeys[i];

			if (key === 'fromAccount') {
				urlFetchTransactions += `&or=${encodeURIComponent(`fromAccount:=:${filtersValues[key]},designatedAccount:=:${filtersValues[key]},(toAccount:=:${filtersValues[key]},type:=:PAYMENT_REFUND)`)}`;
			} else if (Array.isArray(filtersValues[key])) {
				if (filtersValues[key].length) {
					urlFetchTransactions += `&${key}=${(filtersValues[key] as string[]).join(',')}`;
				}
			} else if (filtersValues[key]) {
				urlFetchTransactions += `&${key}=${filtersValues[key]}`;
			}
		}

		// add id param
		if (paramId && tab === 'summary') {
			urlFetchTransactions += `&id=${paramId}`;
		}

		setStatus('LOADING');

		const fetchTransactions = await fetch(urlFetchTransactions, {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		});

		const resTransactions = await fetchTransactions.json();

		if (resTransactions.data) {
			if (urlFetchTransactions.includes('groupBy')) {
				setTransactionsGrouping(resTransactions.data);
			} else {
				const transactionsNew = addDaEquivalentPropsToTransactions({
					loanId: Number(filtersValuesDefaults.loan),
					transactions: resTransactions.data,
				});

				setTransactions(transactionsNew);
			}
		}

		if (resTransactions.pagination) {
			setPaginationTransactions(resTransactions.pagination);
		}

		setStatus('DEFAULT');
	};

	// options
	const optionsAccounts: Option[] = accounts
		.map((account) => {
			return {
				label: <ListItemTextAccount account={account} />,
				secondary: account.institution,
				value: String(account.id),
			};
		})
		.sort((a: Option, b: Option) => {
			return String(a.label) > String(b.label) ? 1 : -1;
		});

	const optionsLoans: Option[] = loans
		.map((loan) => {
			return {
				disabled: loan.status === 'DISABLED',
				label: loan.code,
				MenuItemProps: {
					financier: loan.financier,
				},
				value: String(loan.id),
			};
		})
		.sort((a: Option, b: Option) => {
			return String(a.label) > String(b.label) ? 1 : -1;
		});

	const optionsComponent = components.map((component) => {
		return {
			label: component.name,
			value: String(component.id),
		};
	});

	const optionsCategory = categories.map((category) => {
		return {
			label: category.name,
			value: String(category.id),
		};
	});

	const optionsExpenseType = expenseTypes.map((expenseType) => {
		return {
			label: expenseType.name,
			value: String(expenseType.id),
		};
	});

	const optionsTransactionType = [
		{
			label: translateTransactionType('CASH_BENEFIT', t),
			value: 'CASH_BENEFIT',
		},
		{
			label: translateTransactionType('OPEX', t),
			value: 'OPEX',
		},
		{
			label: translateTransactionType('PAYMENT', t),
			value: 'PAYMENT',
		},
		{
			label: translateTransactionType('PAYMENT_REFUND', t),
			value: 'PAYMENT_REFUND',
		},
	];

	const optionsWa = withdrawalApplications.map((wa) => {
		return {
			label: wa.idExternal,
			value: wa.id,
		};
	});

	// vars
	const idFormFilters = 'form-soe-filters';
	const resetParamsExceptions = [
		'groupColumns',
		'hiddenFilters',
		'isHeadless',
		'isLoginImmediate',
		'loanCode',
		'readOnlyFields',
		'readOnlyFilters',
		'withdrawalApplicationNew',
	];

	// set loan code from search params
	if (paramLoanCode) {
		filtersValues.loan =
			(optionsLoans.find((loan) => loan.label === paramLoanCode)?.value as string) ||
			paramLoan ||
			'';
	}

	const filters: FilterProps[] = [
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-loan`,
			label: t('financingSource'),
			name: 'loan',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'loan'),
			options: optionsLoans,
			readOnly: paramReadOnlyFilters?.includes('loan') || false,
			required: true,
			type: 'SELECT',
			value: filtersValues.loan,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-from-account`,
			label: t('account'),
			name: 'fromAccount',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'fromAccount'),
			options: optionsAccounts,
			required: true,
			type: 'SELECT',
			value: filtersValues.fromAccount,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			helperText: t('dateStart.helper'),
			id: `${idFormFilters}-date-start`,
			InputProps: {
				inputProps: {
					// minimum one year before dateEnd
					min: formatDate({
						date: new Date(
							new Date(filtersValues.dateEnd as string).setFullYear(
								new Date(filtersValues.dateEnd as string).getFullYear() - 1
							)
						),
						format: 'YYYY-MM-DD',
					}),
					// maximum one day before dateEnd
					max: formatDate({
						date: new Date(
							new Date(filtersValues.dateEnd as string).setDate(
								new Date(filtersValues.dateEnd as string).getDate() - 1
							)
						),
						format: 'YYYY-MM-DD',
					}),
				},
				type: 'date',
			},
			label: t('startDate'),
			name: 'dateStart',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'dateStart'),
			required: true,
			type: 'INPUT',
			value: filtersValues.dateStart,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-date-end`,
			InputProps: {
				inputProps: {
					// minimum one day after dateStart
					min: formatDate({
						date: new Date(
							new Date(filtersValues.dateStart as string).setDate(
								new Date(filtersValues.dateStart as string).getDate() + 1
							)
						),
						format: 'YYYY-MM-DD',
					}),
					// maximum one year after dateStart
					max: formatDate({
						date: new Date(
							new Date(filtersValues.dateStart as string).setFullYear(
								new Date(filtersValues.dateStart as string).getFullYear() + 1
							)
						),
						format: 'YYYY-MM-DD',
					}),
				},
				type: 'date',
			},
			label: t('endDate'),
			name: 'dateEnd',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'dateEnd'),
			required: true,
			type: 'INPUT',
			value: filtersValues.dateEnd,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-type`,
			label: t('transactionType'),
			multiple: true,
			name: 'type',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'type'),
			options: optionsTransactionType,
			type: 'SELECT',
			value: filtersValues.type,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-component`,
			label: t('component'),
			multiple: true,
			name: 'component',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'component'),
			options: optionsComponent,
			type: 'SELECT',
			value: filtersValues.component,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-category`,
			label: t('category'),
			multiple: true,
			name: 'category',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'category'),
			options: optionsCategory,
			type: 'SELECT',
			value: filtersValues.category,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-expense-type`,
			label: t('expenseType'),
			multiple: true,
			name: 'expenseType',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'expenseType'),
			options: optionsExpenseType,
			type: 'SELECT',
			value: filtersValues.expenseType,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-tags`,
			label: t('tags'),
			multiple: true,
			name: 'tags',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'tags'),
			type: 'TAGS',
			value: filtersValues.tags,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-amount-min`,
			InputProps: {
				inputProps: { min: 0 },
				type: 'number',
			},
			label: t('minAmount'),
			name: 'amountMin',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'amountMin'),
			type: 'INPUT',
			value: filtersValues.amountMin,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasSearchParam: true,
			id: `${idFormFilters}-amount-max`,
			InputProps: {
				inputProps: { min: 0 },
				type: 'number',
			},
			label: t('maxAmount'),
			name: 'amountMax',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'amountMax'),
			type: 'INPUT',
			value: filtersValues.amountMax,
		},
		{
			GridProps: { lg: 4, xl: 3, xs: 12 },
			hasNoneOption: true,
			hasSearchParam: true,
			helperText: t('borrowerReference#'),
			hidden: paramHiddenFilters?.includes('withdrawalApplication') || false,
			id: `${idFormFilters}-withdrawal-application`,
			label: t('withdrawalApplication'),
			name: 'withdrawalApplication',
			onChange: (e: ChangeFilterEvent) => handleChangeFilter(e, 'withdrawalApplication'),
			options: optionsWa,
			type: 'SELECT',
			value: filtersValues.withdrawalApplication,
		},
	];

	// handlers
	const handleExportReport = async () => {
		if (activeTab === 'summary') {
			searchParams.set('dialog', 'exportSoeReportSummary');
		} else {
			searchParams.set('dialog', 'exportSoeReport');
		}
		setSearchParams(searchParams);
	};

	// Function to handle filter changes
	const handleChangeFilter = (e: ChangeFilterEvent, prop: string) => {
		const { value } = e.target as HTMLInputElement;

		// Immediately update the filters state
		setFiltersValues((prevFilters) => ({
			...prevFilters,
			[prop]: value,
		}));
	};

	const handleGenerateReport = (e: FormEvent) => {
		e.preventDefault();
		getTransactions({ pagination: paginationTransactions, tab: activeTab });
	};

	const handleClickTab = ({ tab }: { tab: string }) => {
		// get transactions if transactions were already fetched
		if (transactions.length || transactionsGrouping.length) {
			getTransactions({
				pagination: paginationTransactions,
				tab,
			});
		}
	};

	const handleGroupByChange = () => {
		// get transactions if transactions were already fetched
		if (transactions.length || transactionsGrouping.length) {
			getTransactions({
				pagination: paginationTransactions,
				tab: 'details',
			});
		}
	};

	// set default filters
	useEffect(() => {
		let searchParamsNew: any = {}; //eslint-disable-line
		const dateToday = new Date();

		// set start and end date if not set
		if (!paramEndDate) {
			searchParamsNew.dateEnd = formatDate({ date: dateToday, format: 'YYYY-MM-DD' });
		}

		if (!paramStartDate) {
			const todayYear = dateToday.getFullYear();
			searchParamsNew.dateStart = formatDate({
				date: new Date(`${todayYear}-01-01`),
				format: 'YYYY-MM-DD',
			});
		}

		// set the default tx types if not set
		if (!paramTxTypes) {
			const defaultTxTypes = ['CASH_BENEFIT', 'OPEX', 'PAYMENT', 'PAYMENT_REFUND'];
			searchParamsNew.type = defaultTxTypes;
		}

		// select the first loan if not set
		if (!paramLoan && optionsLoans.length) {
			searchParamsNew.loan = optionsLoans[0].value.toString();
		}

		// select the first account if not set
		if (!paramFromAccount && optionsAccounts.length) {
			searchParamsNew.fromAccount = optionsAccounts[0].value.toString();
		}

		if (Object.keys(searchParamsNew).length) {
			Object.keys(searchParamsNew).forEach((key) => {
				searchParams.set(key, searchParamsNew[key]);
			});
			setSearchParams(searchParams);
			setFiltersValues((prevFilters) => ({
				...prevFilters,
				...searchParamsNew,
			}));
		}
	}, [
		optionsAccounts,
		optionsLoans,
		paramCurrency,
		paramEndDate,
		paramFromAccount,
		paramLoan,
		paramStartDate,
		paramTxTypes,
		searchParams,
		setSearchParams,
	]);

	// get transactions when groupBy columns change
	return (
		<ContextSoeAccounts.Provider
			value={{
				accounts,
				setAccounts,
			}}>
			<ContextSoeFilters.Provider value={{ filters, filtersValues, setFiltersValues }}>
				<ContextSoeTransactions.Provider
					value={{
						columns,
						getTransactions,
						headerSelections,
						isLoading: status === 'LOADING',
						pagination: paginationTransactions,
						setColumns,
						setHeaderSelections,
						setPagination: setPaginationTransactions,
						setTransactions,
						setTransactionsGrouping,
						setTransactionsUnchecked,
						transactions,
						transactionsGrouping,
						transactionsUnchecked,
					}}>
					<Grid>
						<Container maxWidth={false}>
							<Grid container spacing={theme.spacing(2)}>
								<Grid item xs={12}>
									<Grid alignItems="center" container spacing={1}>
										<Grid item xs={12} lg="auto">
											<Typography variant="h2">{t('soe')}</Typography>
										</Grid>
										{paramWithdrawalApplicationNew && (
											<Grid item xs={12} lg="auto">
												<Typography
													sx={(theme) => {
														return {
															color: theme.palette.brand.magenta[500],
														};
													}}
													variant="subtitle1">
													{paramWithdrawalApplicationNew}
												</Typography>
											</Grid>
										)}
									</Grid>
								</Grid>
								<Grid item xs={12}>
									<SoeFilters
										filters={filters}
										isLoading={status === 'LOADING'}
										onSubmit={handleGenerateReport}
										onReset={() => {
											setFiltersValues(filtersValuesDefaults);
											setTransactions([]);
											searchParams.forEach((value, key) => {
												if (!resetParamsExceptions.includes(key)) {
													searchParams.delete(key);
												}
											});
											setSearchParams(searchParams);
										}}
									/>
								</Grid>
								<>
									<Grid item xs={12}>
										<Grid container spacing={2}>
											<Grid item container justifyContent="space-between" spacing={2}>
												<Grid item xs={12} lg="auto">
													<Grid container spacing={2}>
														<Grid item xs={12} lg="auto">
															<ToggleButtonGroup
																color="primary"
																disabled={status === 'LOADING'}
																exclusive={true}
																fullWidth={true}
																value={activeTab}>
																<ToggleButton
																	component={RouterLink}
																	onClick={() => handleClickTab({ tab: 'details' })}
																	to={`/project/${
																		project?.idWbg
																	}/reports/soe/details?${searchParams.toString()}`}
																	value="details">
																	<IconTableChart sx={{ mr: 1 }} />
																	{t('details')}
																</ToggleButton>
																<ToggleButton
																	component={RouterLink}
																	onClick={() => handleClickTab({ tab: 'summary' })}
																	to={`/project/${
																		project?.idWbg
																	}/reports/soe/summary?${searchParams.toString()}`}
																	value="summary">
																	<IconSummarize sx={{ mr: 1 }} />
																	{t('summary')}
																</ToggleButton>
															</ToggleButtonGroup>
														</Grid>
														{activeTab === 'details' && (
															<Grid item xs={0} sm={12} lg="auto">
																<BtnCheckboxList
																	items={headerSelections}
																	fullWidth
																	label={t('columns')}
																	setItems={setHeaderSelections}
																	onItemsChange={() => {
																		// Header visibility changed
																	}}
																	onReset={() => {
																		setHeaderSelections((prev) =>
																			prev.map((header) => ({
																				...header,
																				checked: [
																					'toEntity',
																					'contract',
																					'contractTotalAmount',
																					'expenseType',
																					'invoiceAmount',
																					'invoiceCurrency',
																					'percentFinanced',
																					'currency',
																					'amountLoan',
																					'taxLoan',
																					'currencyDa',
																					'amountLoanDaEquivalent',
																					'taxLoanDaEquivalent',
																					'totalPaidFromDa',
																					'exchangeRateDa',
																					'date',
																					'comments',
																				].includes(header.key),
																			}))
																		);
																	}}
																	variant="outlined"
																/>
															</Grid>
														)}
														{activeTab === 'summary' && (
															<>
																<Grid item xs={0} sm={12} lg="auto">
																	<BtnSortableList
																		disabled={status === 'LOADING'}
																		items={columns}
																		label={t('groupBy')}
																		setItems={setColumns}
																		variant="outlined"
																		fullWidth
																		onItemsChange={() => handleGroupByChange()}
																	/>
																</Grid>
															</>
														)}
													</Grid>
												</Grid>
												<Grid item xs={12} lg="auto">
													<Grid container spacing={2}>
														<Grid item xs={true}>
															<FormSearch InputProps={{ placeholder: t('searchById') }} />
														</Grid>
														<Grid item xs="auto">
															<Button
																disabled={
																	transactions.length === 0 && transactionsGrouping.length === 0
																}
																variant="contained"
																onClick={handleExportReport}>
																{t('exportSoe')}
															</Button>
														</Grid>
													</Grid>
												</Grid>
											</Grid>
										</Grid>
									</Grid>
									<Grid item xs={12}>
										<Outlet />
									</Grid>
								</>
							</Grid>
							{activeTab === 'summary' ? (
								<DialogExportSoeReportSummary
									accounts={accounts}
									categories={categories}
									components={components}
									expenseTypes={expenseTypes}
									filtersValues={filtersValues}
									isOpen={paramDialog === 'exportSoeReportSummary'}
									loans={loans}
									onClose={() => {
										searchParams.delete('dialog');
										searchParams.delete('exportSoeReportSummary');
										setSearchParams(searchParams);
									}}
									onSuccess={() =>
										getTransactions({ pagination: paginationTransactions, tab: 'summary' })
									}
								/>
							) : (
								<DialogExportSoeReport
									accounts={accounts}
									categories={categories}
									components={components}
									expenseTypes={expenseTypes}
									isOpen={paramDialog === 'exportSoeReport'}
									loans={loans}
									onClose={() => {
										searchParams.delete('dialog');
										searchParams.delete('exportSoeReport');
										setSearchParams(searchParams);
									}}
									onSuccess={() =>
										getTransactions({ pagination: paginationTransactions, tab: 'details' })
									}
								/>
							)}
						</Container>
					</Grid>
				</ContextSoeTransactions.Provider>
			</ContextSoeFilters.Provider>
		</ContextSoeAccounts.Provider>
	);
};
